// ─── Modules, sous-modules & applications de la suite MoP ─────────
// Hiérarchie :
//   APPLICATION  (ce qui est livré/versionné)  ex. mop-empty, mop-cal
//     └─ MODULE      (unité fonctionnelle)     ex. core, cal, inventory
//          └─ SOUS-MODULE (fonctionnalité)     ex. core-connexion, inv-plans
//
//   mop-empty        = core + empty                      (l'app vide, socle de départ)
//   mop-cal          = core + cal + inventory (lecture)  (MoPCal : hub famille)
//   mop-my-inventory = core + inventory (écriture)       (My Inventory)
//   mop-my-finance   = core + finance   (écriture)       (My Finance)
//
// RÈGLE D'INDÉPENDANCE : un module ne dépend que de `core`. Il peut, à titre
// exceptionnel, LIRE un autre module via les droits portés par l'espace
// (ex. cal lit inventory). Toute autre dépendance croisée est un signal d'alarme.
//
// Sert de discriminant partout : origine d'un fait technique (application +
// module + sous-module), suivi de version, droits d'échange de l'espace.

export const MODULES = [
  {
    id: 'core', label: 'Core', emoji: '🧩', desc: 'Socle commun à toutes les apps',
    sousModules: [
      { id: 'core-connexion',     label: 'Connexion',      emoji: '🔑', desc: 'Comptes, inscription, vérification e-mail, bascule de comptes' },
      { id: 'core-espaces',       label: 'Espaces',        emoji: '🏠', desc: 'Le tenant : membres et droits d\'échange inter-apps' },
      // core-profil = l'écran Profil commun + son POINT D'EXTENSION : une app y
      // greffe ses sections propres (prop `sections`) sans que le Core connaisse
      // leur contenu. MoPCal y branchera ses calendriers (« cal-profil »).
      { id: 'core-profil',        label: 'Profil',         emoji: '👤', desc: 'Compte, espaces, apparence, langue, à propos — extensible par l\'app' },
      { id: 'core-theme',         label: 'Thème',          emoji: '🎨', desc: 'Clair/sombre, style d\'icônes, palettes' },
      { id: 'core-i18n',          label: 'Traductions',    emoji: '🌐', desc: 'Catalogues FR/EN + surcharge admin' },
      { id: 'core-navigation',    label: 'Navigation',     emoji: '🧭', desc: 'Coquille, onglets, pied de version, gabarit d\'écran' },
      { id: 'core-pub',           label: 'Publicité',      emoji: '📣', desc: 'Bandeau et interstitiel, pilotés par la config admin' },
      { id: 'core-ivv',           label: 'IVV',            emoji: '🐞', desc: 'Faits techniques, fiches de test, questionnaires, versions' },
      { id: 'core-aide',          label: 'Aide',           emoji: '🎓', desc: 'Aide, tutoriels, mises en pratique' },
      { id: 'core-admin',         label: 'Administration', emoji: '⚙️', desc: 'Config app, utilisateurs, whitelist, e-mails' },
      { id: 'core-forfaits',      label: 'Forfaits',       emoji: '🎟️', desc: 'Plans, paliers, quotas, à la carte, dons' },
      { id: 'core-notifications', label: 'Notifications',  emoji: '🔔', desc: 'Push et badges' },
      { id: 'core-stockage',      label: 'Stockage',       emoji: '📎', desc: 'Upload photos et fichiers' },
      { id: 'core-historique',    label: 'Historique',     emoji: '🕘', desc: 'Journal d\'audit des modifications' },
    ],
  },
  {
    id: 'empty', label: 'Empty', emoji: '⬜', desc: 'Gabarit vide — point de départ d\'une nouvelle app',
    sousModules: [],
  },
  {
    id: 'cal', label: 'Cal', emoji: '📅', desc: 'Organisation familiale',
    sousModules: [
      { id: 'cal-calendrier',   label: 'Calendrier',    emoji: '📅', desc: 'Vues mois/semaine/jour, superposition, filtres' },
      { id: 'cal-evenements',   label: 'Événements',    emoji: '📋', desc: 'Création/édition, récurrences, multi-jours, commentaires' },
      { id: 'cal-garde',        label: 'Garde',         emoji: '🗓️', desc: 'Alternance, modèles, demandes et validation' },
      { id: 'cal-vacances',     label: 'Vacances',      emoji: '🏖️', desc: 'Zones de vacances scolaires' },
      { id: 'cal-edt',          label: 'Emploi du temps', emoji: '🕐', desc: 'Emploi du temps scolaire' },
      { id: 'cal-messagerie',   label: 'Messagerie',    emoji: '✉️', desc: 'Fil de discussion familial' },
      { id: 'cal-contacts',     label: 'Contacts',      emoji: '📇', desc: 'Carnet de contacts partagé' },
      { id: 'cal-lieux',        label: 'Lieux',         emoji: '📍', desc: 'Adresses réutilisables' },
      { id: 'cal-listes',       label: 'Listes',        emoji: '🛒', desc: 'Listes et courses' },
      { id: 'cal-repas',        label: 'Repas',         emoji: '🍲', desc: 'Recettes, repas, menus' },
      { id: 'cal-journal',      label: 'Journal',       emoji: '📓', desc: 'Notes du quotidien' },
      { id: 'cal-statistiques', label: 'Statistiques',  emoji: '📊', desc: 'Répartition de garde, chiffres clés' },
      { id: 'cal-export',       label: 'Export',        emoji: '📤', desc: 'Export .ics et rapport imprimable' },
      // Extension MoPCal de core-profil : calendriers, garde, forfait…
      { id: 'cal-profil',       label: 'Profil MoPCal', emoji: '👤', desc: 'Sections propres à MoPCal greffées sur le Profil du Core' },
    ],
  },
  {
    id: 'inventory', label: 'Inventory', emoji: '📦', desc: 'Stock & rangement',
    sousModules: [
      { id: 'inv-inventaires',  label: 'Inventaires',   emoji: '📦', desc: 'Arbre de catégories, propriétés sur mesure, modèles' },
      { id: 'inv-objets',       label: 'Objets',        emoji: '🧷', desc: 'Éléments / articles, quantités ±, seuils' },
      { id: 'inv-emplacements', label: 'Emplacements',  emoji: '🗄️', desc: 'Arbre des lieux de rangement' },
      { id: 'inv-plans',        label: 'Plans',         emoji: '🗺️', desc: 'Plan 2D : murs, formes, pastilles de comptage' },
      { id: 'inv-comptage',     label: 'Comptage',      emoji: '📋', desc: '« Faire l\'inventaire », dates de comptage' },
      { id: 'inv-scan',         label: 'Scan',          emoji: '📷', desc: 'Code-barres et fiche produit publique' },
      { id: 'inv-photos',       label: 'Photos',        emoji: '🖼️', desc: 'Photos d\'objets et d\'articles' },
      { id: 'inv-io',           label: 'Import/Export', emoji: '📤', desc: 'JSON, PDF, QR d\'emplacement' },
      { id: 'inv-referentiel',  label: 'Référentiel',   emoji: '🏷️', desc: 'Catalogue produits et prix' },
    ],
  },
  {
    id: 'finance', label: 'Finance', emoji: '💰', desc: 'Budget & finances',
    sousModules: [
      { id: 'fin-operations',      label: 'Opérations',      emoji: '💵', desc: 'Dépenses/revenus, postes, attribution' },
      { id: 'fin-recurrentes',     label: 'Récurrentes',     emoji: '🔁', desc: 'Modèles récurrents et projection' },
      { id: 'fin-remboursements',  label: 'Remboursements',  emoji: '🤝', desc: '« Qui doit à qui », dettes' },
      { id: 'fin-import',          label: 'Import bancaire', emoji: '🏦', desc: 'Import et règles apprises' },
      { id: 'fin-synthese',        label: 'Synthèse',        emoji: '📈', desc: 'Synthèses et vues globales' },
    ],
  },
];

export const moduleInfo = (id) =>
  MODULES.find(m => m.id === id) || { id: id || 'inconnu', label: id || 'Inconnu', emoji: '❓', sousModules: [] };

export const sousModulesDe = (moduleId) => moduleInfo(moduleId).sousModules || [];

export function sousModuleInfo(id) {
  for (const m of MODULES) {
    const sm = (m.sousModules || []).find(s => s.id === id);
    if (sm) return { ...sm, module: m.id };
  }
  return { id: id || '', label: id || '', emoji: '', module: '' };
}

// Applications de la suite (ce qui est réellement livré et versionné).
export const APPS = [
  { id: 'mop-empty',        label: 'MoP Empty',    emoji: '⬜', modules: { core: 'write', empty: 'write' } },
  { id: 'mop-cal',          label: 'MoPCal',       emoji: '📅', modules: { core: 'write', cal: 'write', inventory: 'read' } },
  { id: 'mop-my-inventory', label: 'My Inventory', emoji: '📦', modules: { core: 'write', inventory: 'write' } },
  { id: 'mop-my-finance',   label: 'My Finance',   emoji: '💰', modules: { core: 'write', finance: 'write' } },
];
export const appInfo = (id) =>
  APPS.find(a => a.id === id) || { id: id || 'inconnue', label: id || 'Inconnue', emoji: '❓', modules: {} };

// Modules d'une app, sous forme de liste utilisable dans l'UI.
export function modulesDeApp(appOuId) {
  const mods = typeof appOuId === 'string' ? appInfo(appOuId).modules : (appOuId?.modules || {});
  return Object.keys(mods).map(id => ({ ...moduleInfo(id), mode: mods[id] }));
}
